// src/components/SolutionCard.tsx

import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ExternalLink } from 'lucide-react';

import { MultiAgentSolutionData_V10 } from '@dopomogai/types';
import {VisualElement} from '@dopomogai/ui';
import {Badge} from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';

// A simplified type for the props, extracting only what the card needs from the full solution data.
// This makes the component easier to use and test.
export interface SolutionCardData {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  visual: MultiAgentSolutionData_V10['heroSection']['heroVisual'];
  badges?: MultiAgentSolutionData_V10['heroSection']['keyBadges'];
  creatorName?: string;
  version?: string;
  learnMoreUrl?: string; // Optional direct URL for external links
}

interface SolutionCardProps {
  data: SolutionCardData;
  className?: string;
  onAction: (actionType: string, payload?: any) => void;
}

export const SolutionCard: React.FC<SolutionCardProps> = ({
  data,
  className,
  onAction,
}) => {
  const { t } = useTranslation('solution_showcase');

  const handleCardClick = () => {
    if (data.learnMoreUrl) {
      onAction('OPEN_URL', { url: data.learnMoreUrl });
    } else {
      // Navigate to the internal solution page
      onAction('NAVIGATE_TO_SOLUTION', { slug: data.slug });
    }
  };

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the card's click event from firing twice
    handleCardClick();
  };

  const displayBadges = data.badges?.slice(0, 3) || []; // Show a max of 3 badges

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={clsx('relative group', className)}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCardClick()}
      aria-label={`View details for the solution: ${data.name}`}
    >
      {/* The black background that acts as the shadow for the neobrutalist effect */}
      <div className="absolute inset-0 bg-black rounded-xl" />

      {/* The main content card that moves on hover */}
      <div
        className={clsx(
          'relative bg-white rounded-xl border-2 border-black overflow-hidden flex flex-col h-full',
          'transition-transform duration-200 ease-out transform',
          'group-hover:-translate-x-1 group-hover:-translate-y-1',
          'group-active:translate-x-0 group-active:translate-y-0 cursor-pointer'
        )}
      >
        {/* Visual Section */}
        <div className="relative overflow-hidden bg-slate-100 border-b-2 border-black aspect-[4/3]">
          <VisualElement visual={data.visual} className="w-full h-full" />
          {data.version && (
            <div className="absolute top-3 left-3">
              <span className="bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-semibold px-2 py-1 rounded-full border border-slate-300 shadow-sm">
                v{data.version}
              </span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-5 flex flex-col flex-grow">
          <div className="flex-grow">
            <h3 className="text-lg font-bold text-gray-900 leading-tight mb-1">{data.name}</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">{data.tagline}</p>
            
            {/* Badges */}
            {displayBadges.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {displayBadges.map((badge, index) => (
                  <Badge
                    key={`${data.id}-badge-${index}`}
                    textKey={badge.textKey} // Use textKey for i18n
                    icon={renderIcon(badge.icon as string)}
                    accentColor={badge.accentColor}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer with Creator and CTA */}
          <div className="mt-auto pt-4 border-t border-slate-200">
            {data.creatorName && (
              <div className="text-xs text-slate-500 mb-3">
                <span>By </span>
                <span className="font-semibold text-slate-700">{data.creatorName}</span>
              </div>
            )}
            <Button
              textKey="solution_showcase:card.viewSolutionButton" // i18n key
              variant="secondary"
              size="sm"
              fullWidth
              icon={data.learnMoreUrl ? <ExternalLink size={14} /> : <ArrowRight size={14} />}
              iconPosition="right"
              onClick={handleCtaClick}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};